import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { useTranslateDisapprovalReason } from "@babackapp/private-api-client/client/use-translate-disapproval-reason";
import type { ShopDisapprovalReasonDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import { Textarea } from "@babackapp/ui/components/textarea";
import {
  getLocaleConfig,
  TranslatableField,
} from "@babackapp/ui/components/translatable-field";
import { LoaderIcon, WandSparkles } from "lucide-react";
import { useState } from "react";

type LocalizedText = { lang: string; text: string };

export type DisapprovalReasonDraft = {
  label: string;
  help: string;
  message: LocalizedText[];
};

type Props = {
  reason?: ShopDisapprovalReasonDto;
  isSaving: boolean;
  onSave: (draft: DisapprovalReasonDraft) => void;
  onCancel: () => void;
};

const toLocaleMap = (message: LocalizedText[] = []): Record<string, string> =>
  Object.fromEntries(message.map((entry) => [entry.lang, entry.text]));

/** Empty locales are dropped: the API rejects a blank text, and a language the
 * merchant left alone is simply not configured. */
const toLocalizedTexts = (message: Record<string, string>): LocalizedText[] =>
  Object.entries(message)
    .filter(([, text]) => text.trim())
    .map(([lang, text]) => ({ lang, text: text.trim() }));

/**
 * Inline editor, not a dialog: the merchant compares a wording with the ones
 * above it while writing. `message` being the only customer-facing field, every
 * language is editable behind the email editor's locale picker — a generated
 * translation is worth rereading, a short label is not.
 */
export function DisapprovalReasonEditor({
  reason,
  isSaving,
  onSave,
  onCancel,
}: Props) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: translate, isPending: isTranslating } =
    useTranslateDisapprovalReason();

  const locales = shop.preferences.languages;
  const defaultLocale = locales[0];

  const [label, setLabel] = useState(reason?.label ?? "");
  const [help, setHelp] = useState(reason?.help ?? "");
  const [message, setMessage] = useState(toLocaleMap(reason?.message));
  const [editedLocale, setEditedLocale] = useState(defaultLocale);
  const [isTranslationStale, setIsTranslationStale] = useState(false);
  const [hasTranslationFailed, setHasTranslationFailed] = useState(false);

  const filledCount = locales.filter((lang) => message[lang]?.trim()).length;
  const sourceText = message[editedLocale]?.trim() ?? "";

  const onMessageChange = (next: Record<string, string>) => {
    // The other languages were translated from a text that no longer exists.
    if (next[editedLocale] !== message[editedLocale]) {
      setIsTranslationStale(
        locales.some((lang) => lang !== editedLocale && next[lang]?.trim()),
      );
    }
    setMessage(next);
  };

  // Translates from the language on screen, not from a fixed primary one: the
  // merchant writes in whichever one they are comfortable with.
  const onTranslate = async () => {
    const targetLocales = locales.filter((lang) => lang !== editedLocale);
    if (!sourceText || targetLocales.length === 0) return;

    setHasTranslationFailed(false);
    try {
      const { translated } = await translate({
        shopId: shop.id,
        text: sourceText,
        sourceLocale: editedLocale,
        targetLocales,
      });

      // A language the API skipped keeps whatever it had.
      const next = { ...message };
      for (const lang of targetLocales) {
        if (translated[lang]?.trim()) next[lang] = translated[lang];
      }
      setMessage(next);
      setIsTranslationStale(false);
    } catch {
      setHasTranslationFailed(true);
    }
  };

  const canSave = Boolean(label.trim()) && filledCount > 0;

  return (
    <div className="flex flex-col gap-4 rounded-md bg-muted p-4">
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="disapproval-reason-label">
            {t("settings.disapprovalReasons.fields.label")}
          </Label>
          <Input
            id="disapproval-reason-label"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            maxLength={100}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="disapproval-reason-help">
            {t("settings.disapprovalReasons.fields.help")}
          </Label>
          <Input
            id="disapproval-reason-help"
            value={help}
            onChange={(event) => setHelp(event.target.value)}
            maxLength={255}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label>{t("settings.disapprovalReasons.fields.message")}</Label>
          {locales.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!sourceText || isTranslating}
              onClick={() => void onTranslate()}
            >
              {isTranslating ? (
                <LoaderIcon className="size-4 animate-spin" />
              ) : (
                <WandSparkles className="size-4" />
              )}
              {t("settings.disapprovalReasons.actions.translateFrom", {
                locale: getLocaleConfig(editedLocale).label,
              })}
            </Button>
          )}
        </div>
        <TranslatableField
          value={message}
          onChange={onMessageChange}
          locales={locales}
          defaultLocale={defaultLocale}
          onLocaleChange={setEditedLocale}
          renderInput={({ value, onChange }) => (
            <Textarea
              rows={3}
              className="resize-none pr-12"
              value={value}
              onChange={(event) => onChange(event.target.value)}
              maxLength={1000}
            />
          )}
        />
        <p className="text-xs text-muted-foreground">
          {t("settings.disapprovalReasons.fields.messageHelp")}
          {locales.length > 1 &&
            ` ${t("settings.disapprovalReasons.fields.messageLocales", {
              count: filledCount,
              total: locales.length,
            })}`}
        </p>
        {isTranslationStale && (
          <p className="text-xs text-amber-600">
            {t("settings.disapprovalReasons.fields.messageStale")}
          </p>
        )}
        {hasTranslationFailed && (
          <p className="text-xs text-destructive">
            {t("settings.disapprovalReasons.fields.translateFailed")}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!canSave || isSaving}
          onClick={() =>
            onSave({
              label: label.trim(),
              help: help.trim(),
              message: toLocalizedTexts(message),
            })
          }
        >
          {t("misc.save")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isSaving}
          onClick={onCancel}
        >
          {t("misc.cancel")}
        </Button>
      </div>
    </div>
  );
}
